import { call, delay, put, takeLatest, select } from 'redux-saga/effects';
import { merge, update, set, capitalize, get, cloneDeep, isEqual } from 'lodash';

import history from 'browserHistory';
import { getTripFieldsSuccess } from 'modules/common/FillTrip';
import {
  cleanError,
  getNewTripSuccess,
  getTripError,
  getTripSuccess,
  setVisitorName,
} from 'modules/common/CommonTrip/actions';
import { INITIAL_VALUE } from 'modules/common/CommonTrip/hooks/usePrepareValues';
import { dataDocsSelector, tripCommonTripSelector, visitorSelector } from 'modules/common/CommonTrip/selectors';
import {
  ITransportIdentifiers,
  IVisitorWithTransport,
  IVisitorWithTransportFromServer,
  ResponseFaceID,
  Visitor,
} from '../../../../../entities/Visitor/interfaces';
import { VisitorService } from '../../../../../entities/Visitor/services';
import { Document } from '../../../../../entities/Document/interfaces';
import { BlackListService } from '../../../../../entities/BlackList/services';
import { tripDetailsType } from '../../../../../entities/Trip';
// import { VisitorIdsParams } from 'utils/commonTypes';
import {
  GET_DRIVER_DATA_REQUEST,
  GET_DRIVER_ID_REQUEST,
  ADD_TO_BLACKLIST_REQUEST,
  GET_DRIVER_AND_TRANSPORT_DATA_REQUEST,
} from './constants';
import { fieldFaceIDSelector } from './selectors';
import {
  getDrDataSuccess,
  getDrIdReguest,
  getDrDataReguest,
  getDrIdError,
  getDrDataError,
  addToBlackListReguest,
  addToBlackListSuccess,
  clearDrDataField,
  addToBlackListError,
  getDriverAndTransportDataRequest,
} from './actions';

function* getDrData(action: ReturnType<typeof getDrDataReguest>) {
  try {
    const data: Visitor = yield call(VisitorService.getDriverByID, action.payload);

    const dataDocs: Document[] = yield select(dataDocsSelector);
    const dataVisitor: any[] = yield select(visitorSelector);
    const type: tripDetailsType = yield select(fieldFaceIDSelector);

    const initValue = get(INITIAL_VALUE, type);

    const docsDatas = get(data, 'documents');
    let updatedDocs: Document[] = [];

    const isKiosk = history.location.pathname.split('/')[1] === 'kiosk';

    const loaded = {
      type,
      isError: false,
      isFastLine: isKiosk,
    };

    // @ts-ignore
    const trip = yield select(tripCommonTripSelector);

    if (dataVisitor.find((v) => v.id === data.id)) {
      const { fieldName } = dataVisitor.find((v) => v.id === data.id);
      const idx = dataVisitor.findIndex((v) => +v.id === +data.id);

      dataDocs.forEach((d, index) => {
        // @ts-ignore
        // eslint-disable-next-line
        if (d.fieldName === fieldName) delete dataVisitor[index];
        return true;
      });

      // eslint-disable-next-line
      delete dataVisitor[idx];

      set(trip, fieldName, initValue);
      set(trip, `isFastLine${capitalize(fieldName)}`, false);
    }

    set(data, 'isFaceId', true);
    // @ts-ignore
    const updated = update(trip, type, () => data);
    if (isKiosk) set(updated, `isFastLine${capitalize(type)}`, true);

    if (docsDatas) {
      const mergeddocs = cloneDeep(docsDatas);
      mergeddocs.forEach((doc: any) => set(doc, 'fieldName', type));

      updatedDocs = [...dataDocs.filter((d: any) => d.fieldName !== type), ...mergeddocs];
    }

    const visitor = {
      fieldName: type,
      firstName: cloneDeep(get(data, 'firstName', '')),
      middleName: cloneDeep(get(data, 'middleName', '')),
      lastName: cloneDeep(get(data, 'lastName', '')),
      phone: cloneDeep(get(data, 'phone', { id: '', number: '' })),
      id: cloneDeep(get(data, 'id', '')),
    };

    const visitors = dataVisitor.filter((f: any) => !isEqual(f.fieldName, type));

    yield put(setVisitorName([...visitors, visitor]));
    yield put(getNewTripSuccess(updated, updatedDocs));
    yield put(getTripFieldsSuccess({ type, data }, loaded));
    yield put(getDrDataSuccess());

    yield delay(10000);

    yield put(clearDrDataField());
  } catch (error: any) {
    yield put(getTripError(Error(error?.message || 'Помилка підключення до пристрою FaceID!')));
    yield put(getDrDataError(error));

    yield delay(3000);
    yield put(clearDrDataField());
    yield put(cleanError());
  }
}

function* getDriverAndTransportData(action: ReturnType<typeof getDriverAndTransportDataRequest>) {
  try {
    const data: IVisitorWithTransportFromServer = yield call(VisitorService.getDriverByIDOrPhone, action.payload);
    if (data === null) {
      throw new Error('Не вдалося отримати дані. Зверніться до реєстратора');
    }

    const loaded = {
      type: 'driver',
      isError: false,
      isFastLine: false,
    };

    // @ts-ignore
    const trip = yield select(tripCommonTripSelector);

    let trailerIdentifiers: ITransportIdentifiers | null = null;

    if (!data.vehicle) {
      throw new Error('Не вдалось знайти транспорт!');
    }

    trip.vehicle = data.vehicle;
    const vehicleIdentifiers = {
      licensePlate: data.vehicle.licensePlate,
      documentNumber: data.vehicle.document.number,
      documentPictures: data.vehicle.document.pictureFiles
        ? data.vehicle.document.pictureFiles?.map((file) => file?.picture || '')
        : null,
    };

    if (data.trailer) {
      trip.trailer = data.trailer;

      trailerIdentifiers = {
        licensePlate: data.trailer.licensePlate,
        documentNumber: data.trailer.document.number,
        documentPictures: data.trailer.document.pictureFiles
          ? data.trailer.document.pictureFiles?.map((file) => file?.picture || '')
          : null,
      };
    }

    // @ts-ignore
    delete data.vehicle;
    delete data.trailer;

    const dataForVisitor: IVisitorWithTransport = {
      ...data,
      vehiclesIdentifiers: {
        vehicle: vehicleIdentifiers,
      },
    };

    if (trailerIdentifiers && dataForVisitor.vehiclesIdentifiers) {
      dataForVisitor.vehiclesIdentifiers.trailer = trailerIdentifiers;
    }

    set(data, 'isFaceId', !!action.payload.id);

    // @ts-ignore
    const updated = update(trip, 'driver', () => dataForVisitor);

    yield put(getNewTripSuccess(updated, []));
    yield put(getTripFieldsSuccess({ type: 'driver', data: dataForVisitor }, loaded));
    yield put(getDrDataSuccess());

    yield delay(10000);

    yield put(clearDrDataField());
  } catch (error: any) {
    yield put(getTripError(Error(error?.message || 'Помилка підключення до пристрою FaceID!')));
    yield put(getDrDataError(error));
    yield put(clearDrDataField());
    yield put(cleanError());
  }
}

function* getDriverId(action: ReturnType<typeof getDrIdReguest>) {
  // @ts-ignore
  const trip = yield select(tripCommonTripSelector);
  const mergedtrip = merge(trip, action.tripValue);
  const isKiosk = history.location.pathname.split('/')[1] === 'kiosk';

  yield put(getTripSuccess(mergedtrip));

  // if visitor not found clean fields
  const type: tripDetailsType = yield select(fieldFaceIDSelector);
  const dataVisitor: any[] = yield select(visitorSelector);
  const dataDocs: Document[] = yield select(dataDocsSelector);
  const initValue = get(INITIAL_VALUE, type);
  const loaded = {
    type,
    isError: false,
    isFastLine: false,
  };
  const visitors = dataVisitor.filter((f: any) => !isEqual(f.fieldName, type));
  const updatedDocs = dataDocs.filter((f: any) => !isEqual(f.fieldName, type));

  try {
    const response: ResponseFaceID = yield call(VisitorService.getFaceID, action.payload);

    if (response.success) {
      if (isKiosk) {
        yield put(
          getDriverAndTransportDataRequest({
            withTransport: true,
            id: response.user_id || null,
            phoneNumber: null,
          }),
        );
      } else {
        yield put(getDrDataReguest(`${response.user_id}`));
      }
    } else {
      yield put(getDrIdError(Error(response?.description || 'Помилка пристрою FaceID!')));

      const updated = update(trip, type, () => initValue);

      if (isKiosk) {
        yield put(setVisitorName([...visitors]));
        yield put(getTripFieldsSuccess({ type, data: initValue }, loaded));
        yield put(getNewTripSuccess(updated, updatedDocs));
      }

      yield delay(10000);
      yield put(clearDrDataField());
    }
  } catch (error: any) {
    yield put(getDrIdError(Error(error?.message || 'Помилка підключення до пристрою FaceID!')));
    yield put(getTripError(Error(error?.message || 'Помилка підключення до пристрою FaceID!')));

    const updated = update(trip, type, () => initValue);

    if (isKiosk) {
      yield put(setVisitorName([...visitors]));
      yield put(getTripFieldsSuccess({ type, data: initValue }, loaded));
      yield put(getNewTripSuccess(updated, updatedDocs));
    }

    yield delay(3000);
    yield put(clearDrDataField());
    yield put(cleanError());
  }
}

function* addBlackLS(action: ReturnType<typeof addToBlackListReguest>) {
  try {
    // @ts-ignore
    yield call(BlackListService.postBlackList, action.payload);

    yield put(addToBlackListSuccess());
  } catch (error: any) {
    yield put(addToBlackListError(error));
  }
}

export function* tripSagaWatcher() {
  yield takeLatest(GET_DRIVER_DATA_REQUEST, getDrData);
  yield takeLatest(GET_DRIVER_ID_REQUEST, getDriverId);
  yield takeLatest(ADD_TO_BLACKLIST_REQUEST, addBlackLS);
  yield takeLatest(GET_DRIVER_AND_TRANSPORT_DATA_REQUEST, getDriverAndTransportData);
}
